/**
 * Public org branding lookup (no auth): GET /public/orgs/:slug.
 *
 * Distinguishes a definitive "not found" (backend 404 -> the org truly doesn't
 * exist) from any other failure (backend unreachable, 5xx). Callers should only
 * hard-404 on `not_found`; on `error` they should degrade gracefully (never 404
 * a real org because of a transient backend issue).
 */
import { APIClient, APIClientError } from '../APIClient'

export interface PublicOrg {
  name: string
  slug: string
  logoUrl: string | null
}

export type PublicOrgResult =
  | { status: 'found'; org: PublicOrg }
  | { status: 'not_found' }
  | { status: 'error' }

export async function getPublicOrgResult(slug: string): Promise<PublicOrgResult> {
  const path = `/public/orgs/${encodeURIComponent(slug)}`
  try {
    const org = await APIClient.makeAPICall<PublicOrg>('GET', path, {
      noAuthorizationHeader: true,
    })
    return { status: 'found', org }
  } catch (e) {
    if (e instanceof APIClientError && e.statusCode === 404) {
      return { status: 'not_found' }
    }
    // eslint-disable-next-line no-console
    console.error(`[publicOrg] GET ${path} ->`, e)
    return { status: 'error' }
  }
}
